import {
  ChatPanel,
  MessageDestroyTip,
  MessageEncryptedTip,
  ChatPanelBodyPinned,
  IsContacts,
  ChatPanelFooter,
  MessageStrangerCard,
  toaster,
  SessionErrorContent,
  ChatPanelFooterError,
  MediaJoinCall,
} from '@halo/ui-chat-react';
import { useShallowEnhance, useStore } from '@halo/zustand-enhance';
import type { MediaType } from '@halo/chat-sdk';
import { MsgPayloadType, StateEnum } from '@halo/chat-sdk';
import { useRefs } from '@halo/hooks';
import { Fragment } from 'react/jsx-runtime';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { MediaStoreInstance } from '@/store/client';
import { useAppStore } from '@/store/client';
import { useActionsStore } from '@/store/actions';
import { useContactsQuery } from '@/scripts/hooks/useContactsQuery';
import { userApis } from '@/configs/apis/user';
import type { UserCardResponse, UserGroupRelationResponse } from '@/configs/apis/user/types';
import { ReportCardDrawer } from '@/components/ReportCardDrawer';
import ReportNotice from '@/components/ReportNotice';
import { useMediaError } from '@/scripts/hooks';
import { ChatContentHeaderActions } from '../../ChatContent/ChatContentHeaderActions';
import { useContactsHandler } from '../../ChatContent/helpers';
import { ChatSettingDrawer } from '../../ChatContentDrawer/ChatSettingDrawer';
import { SessionMessages } from '../../session-parts/session-messages';
import type { SessionFileDragRef } from '../../session-parts/session-file-drag';
import { SessionFileDrag } from '../../session-parts/session-file-drag';
import type { SessionProps } from '../types';
import {
  SessionBody,
  SessionComposer,
  SessionContentsContextProvider,
  SessionHeader,
  SessionMsgList,
} from '../../session-parts';

export interface SessionSingleContentProps extends SessionProps {
  mediaStoreInstance: MediaStoreInstance | undefined;
}

export function SessionSingleContent({
  visible,
  sessionStoreInstance,
  mediaStoreInstance,
}: SessionSingleContentProps) {
  const { t } = useTranslation();
  const {
    user,
    mediaCall,
    mediaJoin,
    mediaActivatedId,
    hasOtherDeviceJoinCall,
    mediaStores,
    hideSession,
    packageErrorMap,
  } = useAppStore(
    useShallowEnhance([
      'user',
      'mediaCall',
      'mediaJoin',
      'mediaActivatedId',
      'hasOtherDeviceJoinCall',
      'mediaStores',
      'hideSession',
      'packageErrorMap',
    ])
  );

  const { mediaError } = useMediaError();

  const fetchProfileGroup = useActionsStore((state) => state.fetchProfileGroup);
  const fetchProfileUser = useActionsStore((store) => store.fetchProfileUser);

  const [openChatSettingDrawer, setOpenChatSettingDrawer] = useState(false);
  const sessionStore = useStore(sessionStoreInstance)!;

  const { isContact } = useContactsQuery(sessionStore?.session?.user_info?.user_id ?? '');

  const [userCardData, setUserCardData] = useState<{
    userCard: UserCardResponse;
    userGroups: UserGroupRelationResponse['groups'];
    isSend: boolean;
  }>();

  const { onBlockContacts, onAddContacts } = useContactsHandler(
    sessionStore?.session?.user_info ?? null
  );

  const sessionFileDragRef = useRef<SessionFileDragRef>(null);

  const packageError = useMemo(() => {
    return packageErrorMap[sessionStore?.session?.user_info?.user_id || ''];
  }, [packageErrorMap, sessionStore?.session?.user_info?.user_id]);

  const isMe = useMemo(() => {
    return sessionStore?.session?.user_info?.user_id === user?.user_id;
  }, [sessionStore?.session?.user_info?.user_id, user?.user_id]);

  const isDeleteAccount = useMemo(
    () => sessionStore?.session?.user_info?.state === StateEnum.DELETE_ACCOUNT,
    [sessionStore?.session?.user_info?.state]
  );

  const disabledMediaJoin = useMemo(() => {
    return !!(mediaActivatedId && mediaActivatedId !== sessionStore?.session?.chat_id);
  }, [mediaActivatedId, sessionStore?.session?.chat_id]);

  const isCalling = useMemo(() => {
    return (
      !!mediaActivatedId ||
      Object.keys(mediaStores).includes(sessionStore?.session?.chat_id as any) ||
      hasOtherDeviceJoinCall
    );
  }, [hasOtherDeviceJoinCall, mediaActivatedId, mediaStores, sessionStore?.session?.chat_id]);

  const sendDisabledLabel = useMemo(() => {
    if (isDeleteAccount) {
      return 'You can’t message a delete account.';
    }
    return '';
  }, [isDeleteAccount]);

  const chatPanelBodyRef = useRef<HTMLDivElement>(null);

  const handleAvatarClick = useCallback(async () => {
    const userId = sessionStore?.session?.user_info?.user_id;
    if (userId) {
      fetchProfileUser(userId);
    }
  }, [sessionStore?.session?.user_info?.user_id, fetchProfileUser]);

  const getUserMessageCliIds_ = useCallback(async (): Promise<string[]> => {
    if (!sessionStore || !user?.user_id) {
      return [];
    }
    const res = await sessionStore.client.Api.messages.getUserMessageCliIds(
      sessionStore?.session?.chat_id || '',
      user?.user_id
    );
    return Array.isArray(res) ? (res as string[]) : [];
  }, [sessionStore, user?.user_id]);

  const getUserCardData = useCallback(async () => {
    const userId = sessionStore?.session?.user_info?.user_id || '';
    if (!userId) return;
    const [cardRes, groupsRes, selfIds] = await Promise.all([
      userApis.getUserCard(userId),
      userApis.getUserGroupRelation(userId),
      getUserMessageCliIds_(),
    ]);
    const isSend = selfIds.length > 0;
    setUserCardData({
      userCard: cardRes?.data ?? null,
      userGroups: groupsRes?.data?.groups ?? [],
      isSend,
    });
  }, [sessionStore?.session?.user_info?.user_id, getUserMessageCliIds_]);

  const refs = useRefs({ getUserCardData });

  const renderFirstMessage = useCallback(
    (key: string) => {
      return (
        <Fragment key={key}>
          <MessageEncryptedTip />
          <MessageDestroyTip />
          {!isContact && !isMe && (
            <MessageStrangerCard
              duration={sessionStore?.session?.expire_duration}
              avatar={sessionStore?.session?.avatar}
              sessionName={sessionStore?.session?.name || ''}
              userCardData={userCardData}
              is_creator={sessionStore?.session?.is_creator}
            />
          )}
        </Fragment>
      );
    },
    [isContact, isMe, sessionStore?.session, userCardData]
  );

  const handleMediaClick = useCallback(
    (key: MediaType) => {
      switch (key) {
        case MsgPayloadType.MEDIA_AUDIO_CALL:
        case MsgPayloadType.MEDIA_VIDEO_CALL: {
          if (sessionStore) {
            mediaCall({
              session_id: sessionStore.session!.chat_id,
              type: key,
              membersId: [sessionStore.session!.user_info!.user_id],
            }).catch((error) => {
              mediaError(error, 'call');
            });
          }
          break;
        }
        default:
      }
    },
    [sessionStore, mediaCall, mediaError]
  );

  useEffect(() => {
    if (!isContact && !isMe && sessionStore?.activated) {
      const { getUserCardData } = refs.current;
      getUserCardData();
    }
  }, [refs, isContact, isMe, sessionStore?.activated]);

  useEffect(() => {
    if (visible) return;
    setOpenChatSettingDrawer(false);
  }, [visible]);

  const showUserReportExceeded = useMemo(() => {
    const userInfo = sessionStore?.singleUser;
    if (!userInfo) return false;

    if (userInfo.is_risk === '1') {
      return true;
    }

    return false;
  }, [sessionStore?.singleUser]);

  return (
    <SessionContentsContextProvider sessionStoreInstance={sessionStoreInstance}>
      <ChatPanel visible={visible}>
        <SessionHeader
          sessionStore={sessionStore}
          isGroup={false}
          isGroupDisband={false}
          isInGroup={false}
          onAvatarClick={handleAvatarClick}
          extra={
            !isDeleteAccount && (
              <ChatContentHeaderActions
                isGroupError={false}
                sessionStore={sessionStore}
                isGroup={false}
                isMe={isMe}
                isCalling={isCalling}
                onMediaClick={handleMediaClick}
                onSettingClick={() => {
                  setOpenChatSettingDrawer(true);
                }}
              />
            )
          }
        />
        <SessionBody ref={chatPanelBodyRef}>
          {packageError && <SessionErrorContent packageError={packageError} />}
          <SessionFileDrag
            ref={sessionFileDragRef}
            bodyRef={chatPanelBodyRef}
            sessionStoreInstance={sessionStoreInstance}
            sessionName={sessionStore?.session?.name || ''}
            disabled={isDeleteAccount}
            disabledLabel={sendDisabledLabel}
          />
          {!isMe && showUserReportExceeded && (
            <ChatPanelBodyPinned>
              <ReportNotice
                type={showUserReportExceeded ? 'user_report_exceeded' : undefined}
                visible={showUserReportExceeded}
              />
            </ChatPanelBodyPinned>
          )}

          {mediaStoreInstance && (
            <ChatPanelBodyPinned>
              <MediaJoinCall
                disabled={disabledMediaJoin}
                mediaStoreInstance={mediaStoreInstance}
                mediaJoin={async () => {
                  await mediaJoin({
                    session_id: sessionStore.session!.chat_id,
                  }).catch(() => {
                    toaster.error({ title: t('calls.joinFailed') });
                  });
                }}
              />
            </ChatPanelBodyPinned>
          )}
          {!isDeleteAccount && !isContact && (
            <ChatPanelBodyPinned>
              {<IsContacts onBlock={onBlockContacts} onAddContacts={onAddContacts} />}
            </ChatPanelBodyPinned>
          )}
          <SessionMsgList sessionStore={sessionStore}>
            <SessionMessages
              renderFirstMessage={renderFirstMessage}
              sessionStore={sessionStore}
            />
          </SessionMsgList>
          <ChatPanelFooter>
            {isDeleteAccount ? (
              <ChatPanelFooterError>
                {t('chat.cannotMessageADeletedAccount')}
              </ChatPanelFooterError>
            ) : (
              <SessionComposer
                visible={visible}
                isMe={isMe}
                sessionStore={sessionStore}
                onPickFile={sessionFileDragRef.current?.handleSendFile}
              ></SessionComposer>
            )}
          </ChatPanelFooter>
          <ChatSettingDrawer
            open={openChatSettingDrawer}
            setOpenChatSettingDrawer={setOpenChatSettingDrawer}
            sessionStore={sessionStore}
            hideSession={hideSession}
            fetchProfileGroup={fetchProfileGroup}
          />
          <ReportCardDrawer
            closeAllClick={() => setOpenChatSettingDrawer(false)}
            from="user"
            id={sessionStore?.session?.user_info?.user_id || ''}
          />
        </SessionBody>
      </ChatPanel>
    </SessionContentsContextProvider>
  );
}
